import React, { useState } from "react";
import { useDispatch } from "react-redux";

// Icons
import {
  AiOutlineSearch,
  AiOutlineFilePdf,
  AiOutlineFileExcel,
} from "react-icons/ai";

// Services
import { logControlService } from '../../Services/logControl.service';
import { downloadService } from "../../Services/download.service";

// Components
import { ToastComponent } from "../Components/ToastComponent";
import LongTable from "../Components/Tables/LongTable";
import Spinner from "../Components/Spinner";
import JsonToForm from "../Components/Modals/JsonToForm-modal";
import DateRangePickers from "../Components/Forms/DateRangePickers";
import { useTranslation } from "react-i18next";

export default function LogsControl() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  dispatch({ type: "title", value: t("TITLE.LOGS") });

  const [loading, setLoading] = useState(false);
  const [toastParams, setToastParams] = useState('');
  const [logList, setLogList] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [details, setDetails] = useState(null);
  const [operationDescription, setOperationDescription] = useState('');
  const [logDescription, setLogDescription] = useState('');

  const listColumns = [t("LOGS_CONTROL.ID"), t("LOGS_CONTROL.TYPE"), t("LOGS_CONTROL.DESCRIPTION"), t("LOGS_CONTROL.DATE")];
  const listFieldColumns = ["logId", "operationType", "logDescription", "date"];

  const formatDateTime = (date) => {
    const pad = (num) => String(num).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const [initialDateTime, setInitialDateTime] = useState(
    formatDateTime(new Date(new Date().setDate(new Date().getDate() - 2)))
  );

  const [endDateTime, setEndDateTime] = useState(
    formatDateTime(new Date())
  );

  const buildPayload = () => ({
    StartDate: initialDateTime,
    EndDate: endDateTime,
    ...(operationDescription ? { OperationDescription: operationDescription } : {}),
    ...(logDescription ? { LogDescription: logDescription } : {}),
  });

  const onSearch = async () => {
    setLoading(true);
    try {
      const payload = {
        ...buildPayload(),
        PageNumber: 1,
        PageSize: 50,
      };
      const result = await logControlService.search(payload);
      setLogList(result?.items || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onDownloadPdf = async () => {
    setLoading(true);
    try {
      const blob = await logControlService.downloadPdf(buildPayload());
      if (blob) {
        downloadService.downloadApiFile(blob, "pdf", "SummaryLogs");
      } else {
        setToastParams(JSON.stringify({
          show: true,
          title: "Atenção",
          body: "Não foi possível gerar o PDF ou não há dados no período selecionado.",
          color: "Warning",
        }));
      }
    } catch (error) {
      console.error(error);
      setToastParams(JSON.stringify({
        show: true,
        title: "Erro",
        body: "Falha ao gerar o PDF.",
        color: "Danger",
      }));
    } finally {
      setLoading(false);
    }
  };

  const onDownloadXls = async () => {
    setLoading(true);
    try {
      const blob = await logControlService.downloadXls(buildPayload());
      if (blob) {
        downloadService.downloadApiFile(blob, "xls", "SummaryLogs");
      } else {
        setToastParams(JSON.stringify({
          show: true,
          title: "Atenção",
          body: "Não foi possível gerar o Excel ou não há dados no período selecionado.",
          color: "Warning",
        }));
      }
    } catch (error) {
      console.error(error);
      setToastParams(JSON.stringify({
        show: true,
        title: "Erro",
        body: "Falha ao gerar o Excel.",
        color: "Danger",
      }));
    } finally {
      setLoading(false);
    }
  };

  const onTableRowView = (item) => {
    setDetails(item.message);
    setShowDetails(true);
  };

  const handleCloseDetails = () => setShowDetails(false);

  return (
    <>
      <div className='container-xxl flex-grow-1 container-p-y'>

        <ToastComponent params={toastParams} closeToast={() => setToastParams(JSON.stringify({ show: false }))} />
        {loading ? <Spinner /> : ""}

        <JsonToForm
          show={showDetails}
          json={details}
          handleClose={handleCloseDetails}
        />

        <div className="row py-5">

          <div className="col-6">
            <DateRangePickers setInitialDateTime={setInitialDateTime} setEndDateTime={setEndDateTime} />
          </div>

          <div className="col-6">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder={t("LOGS.OPERATION_TYPE")}
                value={operationDescription}
                onChange={(e) => setOperationDescription(e.target.value)}
              />
              <input
                type="text"
                className="form-control"
                placeholder="Descrição do log"
                value={logDescription}
                onChange={(e) => setLogDescription(e.target.value)}
              />
            </div>
          </div>

        </div>

        <div className="row pb-4">
          <div className="col-12 d-flex gap-2">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSearch}
            >
              <AiOutlineSearch className="icon-in-button" />
              {t("LOGS.SEARCH_BUTTON")}
            </button>

            <button
              type="button"
              className="btn btn-danger"
              title="Download PDF"
              onClick={onDownloadPdf}
            >
              <AiOutlineFilePdf className="icon-in-button" />
              PDF
            </button>

            <button
              type="button"
              className="btn btn-success"
              title="Download XLS"
              onClick={onDownloadXls}
            >
              <AiOutlineFileExcel className="icon-in-button" />
              XLS
            </button>
          </div>
        </div>

        {logList?.length > 0 && (
          <LongTable
            data={logList}
            columns={listColumns}
            fields={listFieldColumns}
            onView={onTableRowView}
          />
        )}

      </div>
    </>
  );
}
